// src/ui/LocationPicker.jsx — pick a place to send.
//
// Two ways in, deliberately no third: your current position, or a search. Live
// location is intentionally absent — a browser tab is the wrong thing to publish a
// moving position from (close the tab and the stream dies with no way to tell the
// other side), so the web sends static pins only, exactly as specced.
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, Search, MapPin, Crosshair, Send } from './icons';
import StaticMap from './StaticMap';
import { searchPlaces, reverseGeocode, getCurrentPosition } from '../services/location';

export default function LocationPicker({ onClose, onSend }) {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [picked, setPicked] = useState(null);   // {lat,lng,name,address}
  const [locating, setLocating] = useState(false);
  const [sending, setSending] = useState(false);
  const abortRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // debounced search; an in-flight request is aborted when you keep typing
  useEffect(() => {
    const q = term.trim();
    if (q.length < 2) { setResults([]); setBusy(false); return undefined; }
    setBusy(true);
    const t = setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        setResults(await searchPlaces(q, { signal: ctrl.signal }));
        setError('');
      } catch (e) {
        if (e.name !== 'AbortError') setError(e.message || 'Search failed.');
      } finally {
        setBusy(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [term]);

  const useCurrent = useCallback(async () => {
    setError(''); setLocating(true);
    try {
      const pos = await getCurrentPosition();
      const place = await reverseGeocode(pos.lat, pos.lng);
      setPicked({ lat: pos.lat, lng: pos.lng, name: place?.name || 'Current location', address: place?.address || '' });
      setResults([]); setTerm('');
    } catch (e) {
      setError(e.message || 'Location unavailable.');
    } finally {
      setLocating(false);
    }
  }, []);

  const send = async () => {
    if (!picked || sending) return;
    setSending(true);
    try {
      await onSend(picked);
      onClose();
    } catch (e) {
      setError(e?.message || 'Could not send that location.');
      setSending(false);
    }
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal loc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head loc-head">
          <span>Send location</span>
          <button className="icon-btn" onClick={onClose} title="Close"><X width={18} height={18} /></button>
        </div>

        <div className="loc-search">
          <div className="search-box">
            <Search width={18} height={18} style={{ color: 'var(--text-3)' }} />
            <input
              ref={inputRef}
              placeholder="Search for a place or address"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
            />
            {term && <button className="icon-btn" style={{ width: 26, height: 26 }} onClick={() => setTerm('')}><X width={14} height={14} /></button>}
          </div>
        </div>

        <button className="loc-current" onClick={useCurrent} disabled={locating}>
          <span className="loc-current-icon"><Crosshair width={18} height={18} /></span>
          <span className="loc-current-text">
            <b>{locating ? 'Getting your position…' : 'Send my current location'}</b>
            <em>Accurate to your device&apos;s GPS</em>
          </span>
        </button>

        <div className="modal-body loc-body">
          {error && <div className="loc-error">{error}</div>}

          {picked && (
            <div className="loc-picked">
              <StaticMap lat={picked.lat} lng={picked.lng} zoom={15} height={190} />
              <div className="loc-picked-meta">
                <MapPin width={16} height={16} />
                <div>
                  <div className="loc-name">{picked.name}</div>
                  {picked.address ? <div className="loc-addr">{picked.address}</div> : null}
                  <div className="loc-coords">{picked.lat.toFixed(5)}, {picked.lng.toFixed(5)}</div>
                </div>
              </div>
            </div>
          )}

          {busy && <div className="loc-hint">Searching…</div>}

          {!busy && results.map((r) => (
            <button
              key={r.id}
              className={`loc-result ${picked && picked.lat === r.lat && picked.lng === r.lng ? 'on' : ''}`}
              onClick={() => setPicked(r)}
            >
              <span className="loc-result-pin"><MapPin width={15} height={15} /></span>
              <span className="loc-result-text">
                <b>{r.name}</b>
                {r.address ? <em>{r.address}</em> : null}
              </span>
            </button>
          ))}

          {!busy && !results.length && !picked && term.trim().length >= 2 && !error && (
            <div className="loc-hint">No places matched “{term.trim()}”.</div>
          )}
          {!picked && !term.trim() && !error && (
            <div className="loc-hint">Search above, or send where you are right now.</div>
          )}
        </div>

        <div className="modal-foot">
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={send} disabled={!picked || sending}>
            <Send width={15} height={15} style={{ marginRight: 6, verticalAlign: -2 }} />
            {sending ? 'Sending…' : 'Send location'}
          </button>
        </div>
      </div>
    </div>
  );
}
